import React, { useState, useEffect, useCallback } from 'react';
import {
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Select,
    Col,
    Row,
    Card,
    Upload,
    message,
    Spin,
    Space,
    Typography,
    Divider,
    Tag,
    Alert,
} from 'antd';
import {
    SaveOutlined,
    CloseOutlined,
    UploadOutlined,
    DeleteOutlined,
    FileTextOutlined,
    DollarOutlined,
    CalendarOutlined,
    TagOutlined,
    UserOutlined,
    ShopOutlined,
    TeamOutlined,
    InboxOutlined,
    CheckCircleOutlined,
    FilePdfOutlined,
    FileImageOutlined,
    BankOutlined,
    LinkOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import BankTransactionSelectModal from '../../components/Common/BankTransactionSelectModal';
import TextArea from 'antd/lib/input/TextArea';
import * as adminactions from "store/actions/AuthActions";
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

// Get organization UUID from environment
const ORGANIZATION_UUID = process.env.REACT_APP_ORGANIZATION_UUID;

const ExpenseForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    // State
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [, setDataSource] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [receiptFile, setReceiptFile] = useState(null);
    const [receiptPreview, setReceiptPreview] = useState(null);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const [existingReceipt, setExistingReceipt] = useState(null);
    const [bankTxnModalVisible, setBankTxnModalVisible] = useState(false);
    const [selectedBankTxn, setSelectedBankTxn] = useState(null);

    const isEditMode = !!id;

    // Fetch expense data for edit mode
    const fetchExpense = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        try {
            const response = await adminactions.fetchExpenses(id);
            if (response) {
                setDataSource(response);
                // Set existing receipt info
                if (response.has_receipt && response.receipt_url) {
                    setExistingReceipt({
                        url: response.receipt_url,
                        filename: response.receipt_filename,
                    });
                }
                // Set existing bank transaction reference
                if (response.bank_transaction_data) {
                    setSelectedBankTxn(response.bank_transaction_data);
                } else if (response.bank_transaction) {
                    // If we only have the ID, fetch the transaction details
                    try {
                        const txnResponse = await axios.get(`/accounting/bank-transaction/${response.bank_transaction}/`);
                        if (txnResponse.data) {
                            setSelectedBankTxn(txnResponse.data);
                        }
                    } catch (txnErr) {
                        console.error('Error fetching bank transaction:', txnErr);
                    }
                }
                // Set form values
                form.setFieldsValue({
                    ...response,
                    date: response.date ? dayjs(response.date) : null,
                });
            }
        } catch (err) {
            console.error('Error fetching expense:', err);
            message.error('Failed to load expense data');
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    // Fetch dropdown data
    const fetchDropdownData = useCallback(async () => {
        try {
            // Fetch vendors
            const vendorResponse = await axios.get('/vendors/vendor/', {
                params: { page_size: 1000 }
            });
            setVendors(vendorResponse.data.results || vendorResponse.data || []);

            // Fetch accounts
            const accountResponse = await axios.get('/accounts/account/', {
                params: { page_size: 1000 }
            });
            setAccounts(accountResponse.data.results || accountResponse.data || []);

            // Fetch users
            const userResponse = await axios.get('/user/user/', {
                params: { page_size: 1000 }
            });
            setUsers(userResponse.data.results || userResponse.data || []);

            // Fetch categories
            const categoryResponse = await axios.get('/accounting/expense/categories/');
            setCategories(categoryResponse.data || []);
        } catch (err) {
            console.error('Error fetching dropdown data:', err);
        }
    }, []);

    // Handle bank transaction selection
    const handleBankTxnSelect = (txnId, txnData) => {
        setSelectedBankTxn(txnData);
        // Auto-fill amount, date, and bank_transaction ID from selected bank transaction
        if (txnData) {
            const updates = {
                bank_transaction: txnData.id,
            };
            if (txnData.amount) {
                updates.amount = parseFloat(txnData.amount);
            }
            if (txnData.transaction_date) {
                updates.date = dayjs(txnData.transaction_date);
            }
            form.setFieldsValue(updates);
        } else {
            // Clear bank_transaction when deselected
            form.setFieldsValue({ bank_transaction: null });
        }
    };

    useEffect(() => {
        fetchDropdownData();
        if (id) {
            fetchExpense();
        } else {
            // Set default values for new expense (without category - will be set after categories load)
            form.setFieldsValue({
                date: dayjs(),
                organization: ORGANIZATION_UUID,
            });
        }
    }, [id, fetchExpense, fetchDropdownData, form]);

    // Set default category after categories are loaded from backend
    useEffect(() => {
        if (!id && categories.length > 0) {
            const currentCategory = form.getFieldValue('category');
            if (!currentCategory) {
                // Find 'OTHER' category by code or use the first one as default
                const defaultCategory = categories.find(cat => cat.code === 'OTHER') || categories[0];
                if (defaultCategory) {
                    form.setFieldsValue({ category: defaultCategory.id });
                }
            }
        }
    }, [categories, id, form]);

    // Handle form submission
    const onFinish = async (values) => {
        setSaving(true);
        try {
            const formData = {
                ...values,
                date: values.date ? values.date.format('YYYY-MM-DD') : null,
                organization: values.organization || ORGANIZATION_UUID,
            };

            let response;
            if (isEditMode) {
                response = await adminactions.updateExpenses(formData, id);
                if (response) {
                    // Upload receipt if a new one was selected
                    if (receiptFile) {
                        await uploadReceipt(id);
                    }
                    setShowSuccess(true);
                    setTimeout(() => setShowSuccess(false), 3000);
                    message.success('Expense updated successfully');
                }
            } else {
                response = await adminactions.addExpenses(formData);
                if (response) {
                    // Upload receipt if one was selected
                    if (receiptFile) {
                        await uploadReceipt(response.id);
                    }
                    message.success('Expense created successfully');
                    navigate('/expenses/edit/' + response.id);
                }
            }
        } catch (err) {
            console.error('Error saving expense:', err);
            message.error('Failed to save expense');
        } finally {
            setSaving(false);
        }
    };

    // Upload receipt to expense
    const uploadReceipt = async (expenseId) => {
        if (!receiptFile) return;

        setUploadingReceipt(true);
        try {
            const formData = new FormData();
            formData.append('receipt', receiptFile);

            await axios.post(`/accounting/expense/${expenseId}/upload-receipt/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            message.success('Receipt uploaded successfully');
            setReceiptFile(null);
            setReceiptPreview(null);
            // Refresh expense data to get new receipt URL
            if (isEditMode) {
                fetchExpense();
            }
        } catch (err) {
            console.error('Error uploading receipt:', err);
            message.error('Failed to upload receipt');
        } finally {
            setUploadingReceipt(false);
        }
    };

    // Delete existing receipt
    const deleteReceipt = async () => {
        if (!id) return;

        try {
            await axios.delete(`/accounting/expense/${id}/delete-receipt/`);
            setExistingReceipt(null);
            message.success('Receipt deleted successfully');
        } catch (err) {
            console.error('Error deleting receipt:', err);
            message.error('Failed to delete receipt');
        }
    };

    // Handle file upload
    const handleFileChange = (info) => {
        const file = info.file.originFileObj || info.file;
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                message.error('Invalid file type. Allowed: JPEG, PNG, GIF, PDF');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                message.error('File too large. Maximum size is 5MB');
                return;
            }

            setReceiptFile(file);

            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => setReceiptPreview(e.target.result);
                reader.readAsDataURL(file);
            } else {
                setReceiptPreview(null);
            }
        }
    };

    // Remove selected file
    const removeSelectedFile = () => {
        setReceiptFile(null);
        setReceiptPreview(null);
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(amount || 0);
    };

    // Get file icon based on type
    const getFileIcon = (filename) => {
        if (!filename) return <FileTextOutlined />;
        const ext = filename.split('.').pop().toLowerCase();
        if (['pdf'].includes(ext)) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <FileImageOutlined style={{ color: '#1890ff' }} />;
        return <FileTextOutlined />;
    };

    // Upload props for Dragger
    const uploadProps = {
        name: 'receipt',
        multiple: false,
        accept: '.jpg,.jpeg,.png,.gif,.pdf',
        showUploadList: false,
        beforeUpload: () => false, // Prevent automatic upload
        onChange: handleFileChange,
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spin size="large" tip="Loading expense..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                    date: dayjs(),
                    organization: ORGANIZATION_UUID,
                }}
            >
                <Row gutter={24}>
                    {/* Left Column - Form Fields */}
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <Space>
                                    <DollarOutlined style={{ color: '#1890ff' }} />
                                    <span>{isEditMode ? 'Edit Expense' : 'New Expense Entry'}</span>
                                    {showSuccess && (
                                        <Tag icon={<CheckCircleOutlined />} color="success">
                                            Saved
                                        </Tag>
                                    )}
                                </Space>
                            }
                            extra={
                                <Space>
                                    <Button
                                        icon={<CloseOutlined />}
                                        onClick={() => navigate('/expenses')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        htmlType="submit"
                                        loading={saving}
                                    >
                                        {isEditMode ? 'Update' : 'Save'} Expense
                                    </Button>
                                </Space>
                            }
                        >
                            <Row gutter={16}>
                                {/* Date */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <CalendarOutlined style={{ color: '#8c8c8c' }} />
                                                <span>Date</span>
                                            </Space>
                                        }
                                        name="date"
                                        rules={[{ required: true, message: 'Please select a date' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </Col>

                                {/* Amount */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <DollarOutlined style={{ color: '#8c8c8c' }} />
                                                <span>Amount</span>
                                            </Space>
                                        }
                                        name="amount"
                                        rules={[{ required: true, message: 'Please enter amount' }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            step={0.01}
                                            precision={2}
                                            prefix="₹"
                                            placeholder="0.00"
                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                {/* Vendor */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <ShopOutlined style={{ color: '#8c8c8c' }} />
                                                <span>Vendor</span>
                                            </Space>
                                        }
                                        name="vendor"
                                    >
                                        <Select
                                            placeholder="Select Vendor"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {vendors.map((vendor) => (
                                                <Option key={vendor.id} value={vendor.id}>
                                                    {vendor.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                {/* Account/Client */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <TeamOutlined style={{ color: '#8c8c8c' }} />
                                                <span>Account/Client</span>
                                            </Space>
                                        }
                                        name="account"
                                    >
                                        <Select
                                            placeholder="Select Account"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {accounts.map((account) => (
                                                <Option key={account.id} value={account.id}>
                                                    {account.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                {/* Category */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <TagOutlined style={{ color: '#8c8c8c' }} />
                                                <span>Category</span>
                                            </Space>
                                        }
                                        name="category"
                                        rules={[{ required: true, message: 'Please select a category' }]}
                                    >
                                        <Select
                                            placeholder="Select Category"
                                            loading={categories.length === 0}
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option?.children?.props?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {categories.map((cat) => (
                                                <Option key={cat.id} value={cat.id}>
                                                    <Tag color={cat.color || 'default'}>
                                                        {cat.name}
                                                    </Tag>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                {/* User */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <UserOutlined style={{ color: '#8c8c8c' }} />
                                                <span>User/Employee</span>
                                            </Space>
                                        }
                                        name="user"
                                    >
                                        <Select
                                            placeholder="Select User"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {users.map((user) => (
                                                <Option key={user.id} value={user.id}>
                                                    {user.first_name} {user.last_name} ({user.email})
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Bank Transaction Reference */}
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item
                                        label={
                                            <Space>
                                                <BankOutlined style={{ color: '#8c8c8c' }} />
                                                <span>Bank Transaction Reference</span>
                                            </Space>
                                        }
                                        name="bank_transaction"
                                    >
                                        <div style={{ width: '100%' }}>
                                            {selectedBankTxn ? (
                                                <div style={{
                                                    padding: '8px 12px',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#fff2e8',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}>
                                                    <Space>
                                                        <BankOutlined style={{ color: selectedBankTxn.transaction_type === 'WITHDRAWAL' ? '#f5222d' : '#52c41a' }} />
                                                        <span>
                                                            {dayjs(selectedBankTxn.transaction_date).format('DD/MM/YYYY')} - {selectedBankTxn.remarks?.substring(0, 30) || 'No remarks'}
                                                        </span>
                                                        <Tag color={selectedBankTxn.transaction_type === 'WITHDRAWAL' ? 'red' : 'green'}>
                                                            {selectedBankTxn.transaction_type === 'WITHDRAWAL' ? '-' : '+'}₹{parseFloat(selectedBankTxn.amount).toLocaleString('en-IN')}
                                                        </Tag>
                                                    </Space>
                                                    <Space>
                                                        <Button size="small" type="link" onClick={() => setBankTxnModalVisible(true)}>
                                                            Change
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            danger
                                                            icon={<CloseCircleOutlined />}
                                                            onClick={() => {
                                                                setSelectedBankTxn(null);
                                                                form.setFieldsValue({ bank_transaction: null });
                                                            }}
                                                        />
                                                    </Space>
                                                </div>
                                            ) : (
                                                <Button
                                                    type="dashed"
                                                    icon={<LinkOutlined />}
                                                    onClick={() => setBankTxnModalVisible(true)}
                                                    style={{ width: '100%' }}
                                                >
                                                    Link Bank Transaction
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Public Notes */}
                            <Form.Item
                                label={
                                    <Space>
                                        <FileTextOutlined style={{ color: '#8c8c8c' }} />
                                        <span>Description / Public Notes</span>
                                    </Space>
                                }
                                name="public_notes"
                            >
                                <TextArea
                                    rows={3}
                                    placeholder="Enter expense description (visible in reports)"
                                />
                            </Form.Item>

                            {/* Private Notes */}
                            <Form.Item
                                label={
                                    <Space>
                                        <FileTextOutlined style={{ color: '#8c8c8c' }} />
                                        <span>Private Notes</span>
                                    </Space>
                                }
                                name="private_notes"
                            >
                                <TextArea
                                    rows={2}
                                    placeholder="Internal notes (not visible in reports)"
                                />
                            </Form.Item>

                            {/* Hidden organization field */}
                            <Form.Item name="organization" hidden>
                                <Input />
                            </Form.Item>

                            <Divider />

                            {/* Receipt Upload Section */}
                            <div>
                                <Title level={5}>
                                    <Space>
                                        <UploadOutlined style={{ color: '#1890ff' }} />
                                        Receipt Attachment
                                    </Space>
                                </Title>

                                {/* Existing Receipt */}
                                {existingReceipt && (
                                    <Alert
                                        message={
                                            <Space>
                                                {getFileIcon(existingReceipt.filename)}
                                                <span>Current Receipt: {existingReceipt.filename}</span>
                                            </Space>
                                        }
                                        type="info"
                                        action={
                                            <Space>
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    href={existingReceipt.url}
                                                    target="_blank"
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={deleteReceipt}
                                                >
                                                    Delete
                                                </Button>
                                            </Space>
                                        }
                                        style={{ marginBottom: 16 }}
                                    />
                                )}

                                {/* New Receipt Upload */}
                                {receiptFile ? (
                                    <Card size="small" style={{ marginBottom: 16 }}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Space>
                                                {receiptPreview ? (
                                                    <img
                                                        src={receiptPreview}
                                                        alt="Receipt preview"
                                                        style={{ maxWidth: 100, maxHeight: 100, borderRadius: 4 }}
                                                    />
                                                ) : (
                                                    getFileIcon(receiptFile.name)
                                                )}
                                                <div>
                                                    <Text strong>{receiptFile.name}</Text>
                                                    <br />
                                                    <Text type="secondary">
                                                        {(receiptFile.size / 1024).toFixed(2)} KB
                                                    </Text>
                                                </div>
                                            </Space>
                                            <Button
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={removeSelectedFile}
                                                size="small"
                                            >
                                                Remove
                                            </Button>
                                            {isEditMode && (
                                                <Button
                                                    type="primary"
                                                    icon={<UploadOutlined />}
                                                    onClick={() => uploadReceipt(id)}
                                                    loading={uploadingReceipt}
                                                    size="small"
                                                >
                                                    Upload Now
                                                </Button>
                                            )}
                                        </Space>
                                    </Card>
                                ) : (
                                    <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined style={{ color: '#1890ff' }} />
                                        </p>
                                        <p className="ant-upload-text">
                                            Click or drag file to upload receipt
                                        </p>
                                        <p className="ant-upload-hint">
                                            Support for JPG, PNG, GIF, PDF (max 5MB)
                                        </p>
                                    </Dragger>
                                )}

                                {!isEditMode && receiptFile && (
                                    <Text type="secondary">
                                        Receipt will be uploaded when you save the expense.
                                    </Text>
                                )}
                            </div>
                        </Card>
                    </Col>

                    {/* Right Column - Summary */}
                    <Col xs={24} lg={8}>
                        <Card title="Expense Summary" style={{ marginBottom: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Amount:</Text>
                                    <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                                        {formatCurrency(form.getFieldValue('amount'))}
                                    </Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Category:</Text>
                                    {(() => {
                                        const selectedCategory = categories.find(
                                            (cat) => cat.id === form.getFieldValue('category')
                                        );
                                        return (
                                            <Tag color={selectedCategory?.color || 'default'}>
                                                {selectedCategory?.name || 'Not selected'}
                                            </Tag>
                                        );
                                    })()}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Date:</Text>
                                    <Text>
                                        {form.getFieldValue('date')
                                            ? dayjs(form.getFieldValue('date')).format('MMM DD, YYYY')
                                            : 'Not selected'}
                                    </Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Receipt:</Text>
                                    <Tag color={existingReceipt || receiptFile ? 'green' : 'orange'}>
                                        {existingReceipt || receiptFile ? 'Attached' : 'No Receipt'}
                                    </Tag>
                                </div>
                            </Space>
                        </Card>

                        {/* Policy Reminders */}
                        <Card
                            size="small"
                            style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}
                        >
                            <Title level={5} style={{ color: '#0050b3', marginBottom: 8 }}>
                                Expense Policy Reminders
                            </Title>
                            <ul style={{ color: '#003a8c', paddingLeft: 20, margin: 0 }}>
                                <li>Receipts are required for expenses over ₹2,000</li>
                                <li>Meal allowance is capped at ₹1,500 per day</li>
                                <li>Submit reports by the 25th of each month</li>
                                <li>All expenses must have proper documentation</li>
                            </ul>
                        </Card>
                    </Col>
                </Row>
            </Form>

            <BankTransactionSelectModal
                visible={bankTxnModalVisible}
                onClose={() => setBankTxnModalVisible(false)}
                onSelect={handleBankTxnSelect}
                selectedTransactionId={selectedBankTxn?.id}
                title="Select Bank Transaction for Expense"
            />
        </div>
    );
};

export default ExpenseForm;
